import { useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useCourseJob } from '@/features/ai/useCourseJob'
import { isStartedTranslationRun } from '@/features/ai/jobsSlice'
import { addUserMessage, selectChat } from '@/features/ai/chatSlice'
import { buildChatItems } from '@/features/ai/lib/chatFromEvents'
import { isTerminalStatus } from '@/features/ai/studioTypes'
import { useStartGeneration, type LessonCount } from '@/features/ai/useStartGeneration'
import { useStartBlockEdit } from '@/features/ai/useStartBlockEdit'
import { useAnswerGate } from '@/features/ai/useAnswerGate'
import { useResumeJob } from '@/features/ai/useResumeJob'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { refusedByReview } from '@/features/editor/lib/reviewGuard'

/**
 * Данные панели генерации и правки блоков. Хук ТОНКИЙ: вся механика прогона
 * живёт в B (реестр, опрос, прогресс, разбор отказов), здесь только сборка
 * того, что рисуют чистые компоненты сообщений, и старт ДВУХ операций —
 * генерации курса и правки блока (под-проект E).
 *
 * Мок-движка больше нет: ни таймеров, ни шагов, ни `isThinking` — это были
 * свойства заглушки, а не предметной области. Число событий `content` зависит
 * от числа уроков/блоков, поэтому фиксированный список шагов рисовать нельзя.
 */
export const useAiChat = (courseId: number) => {
  const dispatch = useAppDispatch()
  const {
    job,
    entry,
    progress,
    isPolling,
    isStatusUnknown,
    isLost,
    dismissLost,
    detach,
    isAwaitingAuthor,
  } = useCourseJob(courseId)
  const userMessages = useAppSelector((s) => selectChat(s, courseId))
  const { start, isStarting, failure, reset } = useStartGeneration()
  const {
    start: startEdit,
    isStarting: isStartingEdit,
    failure: editFailure,
    reset: resetEdit,
  } = useStartBlockEdit()
  const { answer, isAnswering, failure: gateFailure } = useAnswerGate()
  const {
    resume,
    isResuming,
    failure: resumeFailure,
    reset: resetResume,
    showResumed,
  } = useResumeJob(courseId)

  const isFrozen = useIsCourseFrozen(courseId)

  const items = useMemo(() => buildChatItems(userMessages, job), [userMessages, job])

  // Пока летит запрос на старт, ВСЁ содержимое реестра принадлежит ПРОШЛОМУ
  // прогону: запись ещё не перецеплена (attachJob происходит только после
  // успешного ответа), а значит и реплика автора, и события джобы, и числа
  // прогресса — от него. Показывать их рядом с индикатором значит выдавать
  // цифры прошлого прогона за новый; гасим все следствия здесь, одним
  // местом, чтобы они не разъезжались. `isStatusUnknown` тут не спасает:
  // ответ джобы A в кеше есть, флаг честно `false`.
  // Старт правки блока — тот же класс гонки, что и старт генерации, поэтому
  // в одном флаге с ним: во время его отправки панель не должна считать
  // себя свободной (главный риск задачи — перепутанный старт).
  const isStale = isStarting || isStartingEdit

  return {
    /**
     * Запись реестра как есть — единственный источник `entry.previous` (F8,
     * §7.4): итог ПЕРВОЙ джобы цепочки живёт в реестре, а не в самой джобе
     * картинок, которую опрашивает `job`.
     */
    entry,
    /** Перевод живёт в меню «Перевод» шапки: панель его не транслирует. Вопрос прогона
     *  показываем всё равно — ответить на него больше негде. */
    hidesTranslation: !isStale && isStartedTranslationRun(entry ?? undefined) && !isAwaitingAuthor,
    items: isStale ? [] : items,
    progress: isStale ? { done: 0, total: null } : progress,
    isRunning: isPolling || isStarting || isStartingEdit,
    /** Статус ещё не подтверждён ответом — числа показывать нельзя. */
    isStatusUnknown,
    /** Пауза: лента рисует карточку вопроса, а трейлинг-индикатор гасится —
     *  «идёт прогон» рядом с вопросом читается как «ответ не нужен». */
    isAwaitingAuthor: !isStale && isAwaitingAuthor,
    /**
     * Ответ на вопрос прогона — проброс `useAnswerGate` до карточки в ленте.
     * `jobId` не идёт по цепочке пропов гейта (`ChatBubble`/`GateCard` знают
     * только сам `gate.id`) — здесь единственное место, где известны оба:
     * джоба, чей кеш патчит `onQueryStarted` в `studioApi` (Important 1), и
     * гейт, который автор нажал.
     */
    answerGate: (gateId: number, optionKey: string) => {
      const jobId = job?.id ?? entry?.jobId
      if (jobId !== undefined) void answer(jobId, gateId, optionKey)
    },
    isAnsweringGate: isAnswering,
    gateFailure,
    /** Итог показываем только на терминальном статусе — до него он лжив. */
    outcome: !isStale && job && isTerminalStatus(job.status) ? job : null,
    /**
     * Тип прогона, за которым СЕЙЧАС следит панель — свой или чужой, после
     * «Показать прогон» (I-4): `job.type` побеждает, как только ответ джобы
     * приехал, `entry.type` держит место до этого момента. Пока летит СВОЙ
     * старт ПРАВКИ (`isStartingEdit`) — тип известен заранее и точно, дефолт
     * прошлой записи тут неуместен (иначе плейсхолдер во время правки лжёт
     * «Генерация...», реф ждёт «Обработка...»). Иначе, пока летит старт
     * генерации или записи вовсе нет, честный дефолт — `course_generation`.
     */
    jobType: isStartingEdit
      ? 'course_block_edit'
      : ((isStale ? undefined : (job?.type ?? entry?.type)) ?? 'course_generation'),
    /** Прогон исчез на бэке — панель обязана объяснить пропажу ленты (M-6). */
    isLost,
    dismissLost,
    failure,
    /** Отказ старта ПРАВКИ — своя переменная: у генерации и правки разные
     *  ручки и разные тела, разъезжаться с общим `failure` им нельзя. */
    editFailure,
    isStartingEdit,
    dismissOutcome: detach,
    /** §8: «Попробовать снова» на итоге — тоже вызов ручки продолжения, не
     *  обычный старт (RunOutcome не знает параметров четырёх типов старта). */
    resumeRun: () => (job && !refusedByReview(isFrozen) ? void resume(job) : undefined),
    /** Курс на проверке: продолжение — тот же прогон с записью в курс, бэк его
     *  на старте не отбивает, и прогон упал бы уже на записи. Кнопки гасит
     *  `ResumeAction`, `resumeRun` страхует клик до прихода `pending`. */
    isFrozen,
    /** §8: `reason: "already_resumed"` — «Показать продолжение», перецепление
     *  сразу на `hint.job_id` без похода в сеть (RunOutcome знает id, но не
     *  тип — тип берёт у текущей джобы, только она и может тут быть). */
    showResumedRun: (jobId: number) => (job ? showResumed(jobId, job.type) : undefined),
    isResuming,
    resumeFailure,
    /**
     * M-1: клик «Показать прогон» обязан гасить баннер отказа — иначе
     * «Курс занят прогоном #45» висит под уже привязанным прогрессом. Сбрасывает
     * ТРИ отказа (ревью Task 3, minor 1; §8, ревью Task 4, Important 1):
     * `handleShowRun` не знает текущий режим панели, а любой из отказов, не
     * сброшенный здесь, пережил бы «Показать прогон» и всплыл заново на итоге
     * ДРУГОГО прогона — `resumeFailure` иначе висел бы под чужой кнопкой
     * «Продолжить» отказом прошлого клика.
     */
    resetFailure: () => {
      reset()
      resetEdit()
      resetResume()
    },
    /**
     * Генерация В СУЩЕСТВУЮЩИЙ курс: языка здесь нет и быть не может —
     * бэк берёт `sourceLang` курса (тип `StartInput` это и запрещает).
     * Возвращает `false`, если старт не состоялся — поле ввода чистит
     * вызвавший, и только по этому ответу.
     */
    send: (prompt: string, lessonCount: LessonCount): Promise<boolean> =>
      start({ prompt, lessonCount, courseId }),
    /**
     * Правка выделенных блоков. Реплику автора кладём тем же `addUserMessage`,
     * что и генерация (§6.1, «одна лента для обеих операций») — только после
     * подтверждённого старта, тем же промптом, который реально ушёл на сервер.
     */
    sendBlockEdit: async (blockIds: number[], prompt: string): Promise<boolean> => {
      const started = await startEdit({ courseId, blockIds, prompt })
      if (!started) return false
      dispatch(
        addUserMessage({
          courseId,
          message: { id: crypto.randomUUID(), text: prompt.trim(), at: Date.now() },
        }),
      )
      return true
    },
  }
}
